import React, { useEffect, useRef, useState } from "react";

const fmt = (n) => {
  if (typeof n !== "number") return "-";
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return n.toFixed(Math.abs(n) < 10 ? 4 : 2);
};

// Mini price sparkline with a hover tooltip showing high/low and % move over the visible window.
export default function Sparkline({ value, up, width = 76, height = 24 }) {
  const [pts, setPts] = useState(() => (typeof value === "number" ? [value] : []));
  const [hover, setHover] = useState(false);
  const last = useRef(value);
  useEffect(() => {
    if (typeof value !== "number") return;
    last.current = value;
    setPts((p) => [...p, value].slice(-28));
  }, [value]);

  if (pts.length < 2) {
    return (
      <svg width={width} height={height} className="inline-block align-middle" aria-hidden="true">
        <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="#CBD5E1" strokeWidth="1" strokeDasharray="2 2" />
      </svg>
    );
  }
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const range = max - min || 1;
  const first = pts[0];
  const curr = pts[pts.length - 1];
  const pct = first ? ((curr - first) / first) * 100 : 0;
  const step = width / (pts.length - 1);
  const y = (v) => height - 2 - ((v - min) / range) * (height - 4);
  const line = pts.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const color = up ? "#059669" : "#DC2626";
  const area = `${line} L${width.toFixed(1)},${height} L0,${height} Z`;

  return (
    <span
      className="relative inline-flex items-center justify-center"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="sparkline"
    >
      <svg width={width} height={height} className="inline-block align-middle cursor-help" aria-hidden="true">
        <path d={area} fill={color} fillOpacity="0.09" stroke="none" />
        <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      {hover && (
        <span
          role="tooltip"
          data-testid="sparkline-tooltip"
          className="absolute z-40 bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md bg-[#06122E] text-white text-[11px] leading-tight px-2.5 py-1.5 shadow-lg border border-[#D8AE4E]/40"
        >
          <span className="block font-mono text-slate-200">H {fmt(max)} · L {fmt(min)}</span>
          <span className={`block font-semibold ${pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {pct >= 0 ? "+" : ""}{pct.toFixed(2)}% <span className="text-slate-400 font-normal">recent</span>
          </span>
        </span>
      )}
    </span>
  );
}
